import LogoBaseController from "./logo_base_controller.js";
import SiteBuilderLogoSaveController from "../site_builder/library_logo_upload_save_controller";
import CustomizationRequest from "../site_builder/customization/request";
import { extractLibraryFileId } from "./utils/get_library_file_id.js";
import { memoizeFIFO } from "../helpers/memoize.js";
import { LogoUploadTabManager } from "../helpers/logo_upload_tab_manager.js";

// Connects to data-controller="logo--library-logo-upload"
export default class extends LogoBaseController {
  static targets = [
    "mediaImageUploader",
    "nextBtn",
    "cropSection",
    "croppedImage",
    "cropperImageContainer",
    "loader",
    "title",
    "selectedThumb",
    "thumbImg",
    "filename"
  ];

  static values = {
    fromSiteBuilder: Boolean,
  };

  initialize() {
    super.initialize();
    this.mediaImageUploader = $(this.mediaImageUploaderTarget);
    this.cropperSection = $(this.cropSectionTarget);
    this.cropperImageContainer = this.cropperSection.find(
      ".media__cropper-image-container"
    );
    this.showImageDrawer();
    this.urlToBase64Store = memoizeFIFO(this.fetchImageAsBase64, { size: 3 });
  }

  async search(e) {
    $(e.target).typeWatch({
      wait: 750,
      captureLength: 1,
      callback: async function () {
        const selectedElement = $(this).parent().parent();
        const url = `${selectedElement.attr(
          "action"
        )}?${selectedElement.serialize()}`;
        await CustomizationRequest.turboFetch(url);
      },
    });
  }

  save() {
    this.fromSiteBuilderValue ? this.saveSiteBuilderLogo() : this.saveCmsAppearanceLogo();
  }

  saveSiteBuilderLogo() {
    this.persistLogoCropperState({
      containerSelector: '.brand-logo',
      cropperInstance: this.mediaSelectCropper,
      imageUrl: this.thumbImgTarget.getAttribute('data-large-url'),
      imageName: this.getImageName(),
    });

    SiteBuilderLogoSaveController.save(
      this.mediaSelectCropper,
      this.thumbImgTarget,
      this.titleTarget,
      this.application
    );
    this.dispatch("image_saved");
  }

  saveCmsAppearanceLogo() {
    this.persistLogoCropperState({
      containerSelector: '.site-logo-images',
      cropperInstance: this.mediaSelectCropper,
      imageUrl: this.thumbImgTarget.getAttribute('data-large-url'),
      imageName: this.getImageName(),
    });

    const isCropped = this.cropperHasChanges();
    if (isCropped) {
      this.applyLogoFromCropper(
        this.mediaSelectCropper,
        this.getImageName()
      );
      this.showProcessingLoader();
    } else {
      const selectedFromLibrary = this.thumbImgTarget.getAttribute('data-selected-from-library') === 'true';
      if (!selectedFromLibrary && window.blobs?.logo) {
        window.modal.close();
        return;
      }

      const imageUrl = this.thumbImgTarget.getAttribute("src");
      const libraryFileId = extractLibraryFileId(imageUrl);
      this.updateHiddenField("#site_logo_library_file_id", libraryFileId);
      this.updateHiddenField("#site_logo_preview_name", this.getImageName());
      this.updateHiddenField("#site_logo_preview_url", '');
      const form = document.querySelector('form.header_form');
      if (form) {
        form.requestSubmit();
      }
      this.showProcessingLoader();
    }
  }

  cancel() {
    this.goBack(this.cropSectionTarget, this.mediaImageUploaderTarget);
    if (this.mediaSelectCropper) {
      this.mediaSelectCropper.reset_zoom_slider();
    }
  }

  async fetchImageAsBase64(url) {
    const data = await fetch(url, { headers: { "Cache-Control": "no-cache" } });
    const blob = await data.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
    });
  }

  async imageUrlToBase64() {
    const thumbImgElement = this.thumbImgTarget;
    const updatedURL = thumbImgElement.getAttribute('data-large-url');

    if (this.fromSiteBuilderValue) {
      this.filenameTarget.textContent = this.getImageName();
    }

    const base64data = await this.urlToBase64Store(updatedURL);

    try {
      await this.setupImageForCropping(
        this.cropperImageContainer,
        base64data
      );
      this.mediaSelectCropper = this.initializeLogoCropper(
        this.cropperSection
      );
      this.hideLoader(this.loaderTarget);
      this.goForward(this.mediaImageUploaderTarget, this.cropSectionTarget);
      return base64data;
    } catch (e) {
      this.hideLoader(this.loaderTarget);
      return;
    }
  }

  getImageName() {
    return this.titleTarget.innerText;
  }

  restoreThumbImageAttributes(thumbImg, largerImageUrl) {
    if (!thumbImg || !largerImageUrl) return;

    thumbImg.setAttribute('data-large-url', largerImageUrl);
    thumbImg.setAttribute('src', largerImageUrl);

    let baseUrl = largerImageUrl;
    let originalUrl = largerImageUrl;

    if (largerImageUrl.includes('/large/')) {
      baseUrl = largerImageUrl.replace('/large/', '/small/');
      originalUrl = largerImageUrl.replace('/large/', '/original/');
    }

    thumbImg.setAttribute('data-small-url', baseUrl);
    thumbImg.setAttribute('data-original-url', originalUrl);
  }
}

document.addEventListener('logoModalContentReady', async () => {
  const hiddenDiv = document.querySelector('.logo-cropper-details');
  if (hiddenDiv) {
    const activeTab = hiddenDiv.getAttribute('data-active-tab');
    const largerImageUrl = hiddenDiv.getAttribute('data-large-image-url');
    const cropperDetails = JSON.parse(hiddenDiv.getAttribute('data-cropper-details'));
    const originalImageName = hiddenDiv.getAttribute('data-original-image-name');
    const zoom = cropperDetails.zoom;

    LogoUploadTabManager.updateFileName(originalImageName);

    LogoUploadTabManager.switchToTab(activeTab, {
      largerImageUrl,
      cropperDetails,
      originalImageName,
      zoom,
      onControllerReady: (logoController) => {
        // Restore the image URLs to thumbImgTarget for library logos
        if ((activeTab === 'media-library' || activeTab === 'media_library') && logoController.thumbImgTarget) {
          logoController.restoreThumbImageAttributes(logoController.thumbImgTarget, largerImageUrl);
        }

        LogoUploadTabManager.openCropper(logoController, {
          largerImageUrl,
          cropperDetails,
          originalImageName,
          zoom
        });
      }
    });
  }
});
